/**
 * LevelStatsTable - Strategy DNA Level Statistics Pivot Table
 *
 * Displays bounce/break/fakeout rates by level/condition combinations.
 * Supports filtering, sorting, and drill-down to event details.
 *
 * Height: ~150px max, scrollable
 */

import React, { useState, useMemo, memo } from 'react';
import { Box, Typography, Tooltip, Chip, IconButton } from '@mui/material';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import FilterListIcon from '@mui/icons-material/FilterList';

/**
 * Format trend direction for compact display
 */
const formatTrend = (trend) => {
    if (!trend) return '--';
    return trend
        .replace('STRONG_UPTREND', 'S.UP')
        .replace('MODERATE_UPTREND', 'M.UP')
        .replace('STRONG_DOWNTREND', 'S.DN')
        .replace('MODERATE_DOWNTREND', 'M.DN')
        .replace('NEUTRAL', 'NEUT')
        .replace('UNKNOWN', '--');
};

/**
 * Format ADX range for compact display
 */
const formatAdxRange = (range) => {
    if (!range) return '--';
    return range
        .replace('STRONG_TREND', 'STR')
        .replace('GOOD_TREND', 'GOOD')
        .replace('WEAK_TREND', 'WEAK')
        .replace('RANGING', 'RNG');
};

/**
 * Get rate color
 */
const getRateColor = (rate, type = 'bounce') => {
    if (rate == null) return '#6b7280';

    if (type === 'fakeout') {
        // Inverse - lower is better
        if (rate <= 15) return '#22c55e';
        if (rate <= 30) return '#fbbf24';
        return '#ef4444';
    }

    // Bounce rate - higher is better
    if (rate >= 60) return '#22c55e';
    if (rate >= 40) return '#fbbf24';
    return '#ef4444';
};

/**
 * Header cell component
 */
const HeaderCell = memo(({ children, width, sortable, sorted, sortDir, onSort, align = 'center' }) => (
    <Box
        onClick={sortable ? onSort : undefined}
        sx={{
            width,
            minWidth: width,
            maxWidth: width,
            display: 'flex',
            alignItems: 'center',
            justifyContent: align === 'left' ? 'flex-start' : 'center',
            gap: 0.25,
            cursor: sortable ? 'pointer' : 'default',
            '&:hover': sortable ? { color: '#dc2626' } : {}
        }}
    >
        <Typography
            sx={{
                fontSize: '8px',
                fontWeight: 700,
                color: sorted ? '#dc2626' : '#9ca3af',
                fontFamily: 'monospace',
                textTransform: 'uppercase',
                whiteSpace: 'nowrap'
            }}
        >
            {children}
        </Typography>
        {sorted && (
            sortDir === 'asc' ?
                <ArrowUpwardIcon sx={{ fontSize: 10, color: '#dc2626' }} /> :
                <ArrowDownwardIcon sx={{ fontSize: 10, color: '#dc2626' }} />
        )}
    </Box>
));

HeaderCell.displayName = 'HeaderCell';

/**
 * Data cell component
 */
const DataCell = memo(({ children, width, color = '#fff', bold = false, align = 'center', warning = false }) => (
    <Box
        sx={{
            width,
            minWidth: width,
            maxWidth: width,
            display: 'flex',
            alignItems: 'center',
            justifyContent: align === 'left' ? 'flex-start' : 'center',
            gap: 0.25
        }}
    >
        <Typography
            sx={{
                fontSize: '10px',
                fontWeight: bold ? 700 : 600,
                color,
                fontFamily: 'monospace',
                whiteSpace: 'nowrap'
            }}
        >
            {children}
        </Typography>
        {warning && <WarningAmberIcon sx={{ fontSize: 10, color: '#ef4444' }} />}
    </Box>
));

DataCell.displayName = 'DataCell';

/**
 * Filter chip component
 */
const FilterChip = memo(({ label, active, onClick }) => (
    <Chip
        label={label}
        size="small"
        onClick={onClick}
        sx={{
            height: '18px',
            fontSize: '9px',
            fontFamily: 'monospace',
            backgroundColor: active ? 'rgba(220, 38, 38, 0.2)' : 'rgba(55, 65, 81, 0.3)',
            color: active ? '#dc2626' : '#9ca3af',
            border: active ? '1px solid #dc2626' : '1px solid transparent',
            '&:hover': { backgroundColor: 'rgba(220, 38, 38, 0.15)' }
        }}
    />
));

FilterChip.displayName = 'FilterChip';

/**
 * LevelStatsTable Component
 */
const LevelStatsTable = memo(({
    stats = [],
    onRowClick,
    filters = {},
    onFilterChange,
    maxHeight = 150
}) => {
    const [sortField, setSortField] = useState('bounceRate');
    const [sortDir, setSortDir] = useState('desc');
    const [activeFilter, setActiveFilter] = useState(null);

    // Column widths
    const cols = {
        level: '70px',
        condition: '50px',
        events: '45px',
        bounce: '50px',
        break: '45px',
        fake: '50px',
        mfe: '50px',
        mae: '50px'
    };

    // Sort handler
    const handleSort = (field) => {
        if (sortField === field) {
            setSortDir(sortDir === 'asc' ? 'desc' : 'asc');
        } else {
            setSortField(field);
            setSortDir('desc');
        }
    };

    // Filter options
    const levelTypes = useMemo(() => {
        const types = new Set(stats.map(s => s.levelType).filter(Boolean));
        return Array.from(types);
    }, [stats]);

    // Sorted and filtered stats
    const sortedStats = useMemo(() => {
        let filtered = [...stats];

        // Apply active filter
        if (activeFilter) {
            filtered = filtered.filter(s => s.levelType === activeFilter);
        }

        // Sort
        filtered.sort((a, b) => {
            const aVal = a[sortField] ?? -Infinity;
            const bVal = b[sortField] ?? -Infinity;
            return sortDir === 'asc' ? aVal - bVal : bVal - aVal;
        });

        return filtered;
    }, [stats, sortField, sortDir, activeFilter]);

    // No data state
    if (!stats || stats.length === 0) {
        return (
            <Box sx={{ padding: '16px', textAlign: 'center', backgroundColor: '#0a0a0a', borderRadius: '2px' }}>
                <Typography sx={{ fontSize: '10px', color: '#6b7280', fontFamily: 'monospace' }}>
                    No level statistics available
                </Typography>
            </Box>
        );
    }

    return (
        <Box sx={{ backgroundColor: '#0a0a0a', borderRadius: '2px', overflow: 'hidden' }}>
            {/* Filter Chips */}
            {levelTypes.length > 1 && (
                <Box
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 0.5,
                        padding: '4px 8px',
                        borderBottom: '1px solid rgba(55, 65, 81, 0.3)',
                        backgroundColor: 'rgba(55, 65, 81, 0.05)'
                    }}
                >
                    <FilterListIcon sx={{ fontSize: 12, color: '#6b7280' }} />
                    <FilterChip
                        label="ALL"
                        active={!activeFilter}
                        onClick={() => setActiveFilter(null)}
                    />
                    {levelTypes.map(type => (
                        <FilterChip
                            key={type}
                            label={type}
                            active={activeFilter === type}
                            onClick={() => setActiveFilter(activeFilter === type ? null : type)}
                        />
                    ))}
                </Box>
            )}

            {/* Table Header */}
            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    padding: '4px 8px',
                    backgroundColor: 'rgba(55, 65, 81, 0.1)',
                    borderBottom: '1px solid rgba(55, 65, 81, 0.3)',
                    gap: '4px'
                }}
            >
                <HeaderCell width={cols.level} align="left">LEVEL</HeaderCell>
                <HeaderCell width={cols.condition}>COND</HeaderCell>
                <HeaderCell
                    width={cols.events}
                    sortable
                    sorted={sortField === 'totalEvents'}
                    sortDir={sortDir}
                    onSort={() => handleSort('totalEvents')}
                >
                    #
                </HeaderCell>
                <HeaderCell
                    width={cols.bounce}
                    sortable
                    sorted={sortField === 'bounceRate'}
                    sortDir={sortDir}
                    onSort={() => handleSort('bounceRate')}
                >
                    BOUNCE
                </HeaderCell>
                <HeaderCell
                    width={cols.break}
                    sortable
                    sorted={sortField === 'breakRate'}
                    sortDir={sortDir}
                    onSort={() => handleSort('breakRate')}
                >
                    BREAK
                </HeaderCell>
                <HeaderCell
                    width={cols.fake}
                    sortable
                    sorted={sortField === 'fakeoutRate'}
                    sortDir={sortDir}
                    onSort={() => handleSort('fakeoutRate')}
                >
                    FAKE
                </HeaderCell>
                <HeaderCell
                    width={cols.mfe}
                    sortable
                    sorted={sortField === 'avgMfePips'}
                    sortDir={sortDir}
                    onSort={() => handleSort('avgMfePips')}
                >
                    MFE
                </HeaderCell>
                <HeaderCell
                    width={cols.mae}
                    sortable
                    sorted={sortField === 'avgMaePips'}
                    sortDir={sortDir}
                    onSort={() => handleSort('avgMaePips')}
                >
                    MAE
                </HeaderCell>
            </Box>

            {/* Table Body - Scrollable */}
            <Box sx={{ maxHeight, overflowY: 'auto' }}>
                {sortedStats.map((stat, index) => {
                    const levelLabel = `${stat.levelType} ${stat.levelValue || ''}`.trim();
                    const condition = formatTrend(stat.trendDirection) || formatAdxRange(stat.adxRange) || '--';
                    const isHighFakeout = (stat.fakeoutRate || 0) > 30;

                    return (
                        <Box
                            key={`${stat.levelType}-${stat.levelValue}-${stat.trendDirection}-${index}`}
                            onClick={() => onRowClick?.(stat)}
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                padding: '5px 8px',
                                gap: '4px',
                                cursor: onRowClick ? 'pointer' : 'default',
                                backgroundColor: isHighFakeout ? 'rgba(239, 68, 68, 0.05)' : 'transparent',
                                borderLeft: isHighFakeout ? '2px solid #ef4444' : '2px solid transparent',
                                borderBottom: '1px solid rgba(55, 65, 81, 0.15)',
                                '&:hover': { backgroundColor: 'rgba(55, 65, 81, 0.1)' },
                                '&:last-child': { borderBottom: 'none' }
                            }}
                        >
                            <Tooltip title={`${stat.levelType} level at ${stat.levelValue}`}>
                                <Box>
                                    <DataCell width={cols.level} align="left" color="#fff" bold>
                                        {levelLabel}
                                    </DataCell>
                                </Box>
                            </Tooltip>
                            <Tooltip title={stat.trendDirection || stat.adxRange || 'No condition'}>
                                <Box>
                                    <DataCell width={cols.condition} color="#9ca3af">
                                        {condition}
                                    </DataCell>
                                </Box>
                            </Tooltip>
                            <DataCell width={cols.events} color="#6b7280">
                                {stat.totalEvents || 0}
                            </DataCell>
                            <DataCell width={cols.bounce} color={getRateColor(stat.bounceRate, 'bounce')} bold>
                                {stat.bounceRate?.toFixed(0) ?? '--'}%
                            </DataCell>
                            <DataCell width={cols.break} color="#9ca3af">
                                {stat.breakRate?.toFixed(0) ?? '--'}%
                            </DataCell>
                            <DataCell
                                width={cols.fake}
                                color={getRateColor(stat.fakeoutRate, 'fakeout')}
                                bold={isHighFakeout}
                                warning={isHighFakeout}
                            >
                                {stat.fakeoutRate?.toFixed(0) ?? '--'}%
                            </DataCell>
                            <DataCell
                                width={cols.mfe}
                                color={stat.avgMfePips >= 0 ? '#22c55e' : '#ef4444'}
                            >
                                {stat.avgMfePips != null ? `${stat.avgMfePips >= 0 ? '+' : ''}${stat.avgMfePips.toFixed(1)}` : '--'}
                            </DataCell>
                            <DataCell
                                width={cols.mae}
                                color={stat.avgMaePips <= 0 ? '#ef4444' : '#22c55e'}
                            >
                                {stat.avgMaePips != null ? stat.avgMaePips.toFixed(1) : '--'}
                            </DataCell>
                        </Box>
                    );
                })}
            </Box>

            {/* Footer: Row count */}
            <Box
                sx={{
                    padding: '3px 8px',
                    backgroundColor: 'rgba(55, 65, 81, 0.05)',
                    borderTop: '1px solid rgba(55, 65, 81, 0.3)'
                }}
            >
                <Typography sx={{ fontSize: '8px', color: '#6b7280', fontFamily: 'monospace' }}>
                    Showing {sortedStats.length} of {stats.length} level combinations
                    {activeFilter && ` (filtered: ${activeFilter})`}
                </Typography>
            </Box>
        </Box>
    );
});

LevelStatsTable.displayName = 'LevelStatsTable';

export default LevelStatsTable;
